'use client';

import { CreatorInfo } from '@ps/types/creator';
import { RemoteAvatar } from '@ps/ui/atoms/RemoteAvatar';

import { useLazyStoreStats } from '../../bio/hooks/useStoreStats';
import { formattedCount } from '../functions/formattedCount';
import {
  categoryTitles,
  useStoreCategories,
} from '../hooks/useStoreCategories';

interface Props {
  creatorInfo: CreatorInfo;
  /** Show the tappable "(Following)" unfollow affordance — set whenever the
   *  viewer follows (RN's banner `FollowingStatus`, which unfollows on tap
   *  regardless of subscription; the cell's Unfollow button is the one gated by
   *  `canUnfollow`). */
  showUnfollow?: boolean;
  /** Unfollow action. Tapping "(Following)" runs it. */
  onUnfollow?: () => void;
}

/**
 * The creator header at the top of the subscribe modal — mirrors PopshopRN's
 * `CreatorProfileBanner` (followers-badge variant): avatar + name (+ tappable
 * "(Following)" when followed) + a subtitle line, with a "N Subscribers" pill
 * from the public seller metrics (`followerCnt`).
 *
 * Subtitle: RN shows the creator's store *categories* (joined titles, e.g.
 * "Glamour"), falling back to the store/profile `bio`. NOT `businessType`.
 */
export function SubscribeCreatorBanner({
  creatorInfo,
  showUnfollow,
  onUnfollow,
}: Props) {
  const creatorId = creatorInfo.id;
  const stats = useLazyStoreStats(creatorId);
  const followerCount = stats?.followerCnt ?? 0;
  const categories = useStoreCategories(creatorId);
  const subtitle = categoryTitles(categories) ?? creatorInfo.bio;

  return (
    <div className="relative flex items-center gap-3 rounded-xl border border-gray-200 p-3">
      <RemoteAvatar
        src={creatorInfo.avatar}
        fallback={(creatorInfo.name ?? '?').charAt(0).toUpperCase()}
        alt={creatorInfo.name}
        size={48}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-content-1 truncate text-base font-medium">
            {creatorInfo.name || 'Creator'}
          </span>
          {showUnfollow && (
            <button
              type="button"
              onClick={onUnfollow}
              disabled={!onUnfollow}
              className="text-content-3 shrink-0 text-[10px] disabled:cursor-default"
            >
              (Following)
            </button>
          )}
        </div>
        {subtitle && (
          <span className="text-content-4 truncate text-xs">{subtitle}</span>
        )}
      </div>
      {followerCount > 0 && (
        <span className="text-content-1 bg-surface-3 absolute right-2 top-1.5 rounded px-1 py-0.5 text-[8px]">
          {formattedCount(followerCount)} Subscribers
        </span>
      )}
    </div>
  );
}
